import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";
import { ActionHandlerFunction } from "@preveil-api";
import {
  Account, useAppSelector, DefaultCollectionName, DefaultRoutes, PathInfo, CollectionEntity, isSameIdentifier, useFetchLinkItem, buildNavigationLink, DriveEntryType,
  DriveConstants
} from "src/common";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  id?: string;
  path_info: PathInfo[];
  collection_info?: CollectionEntity;
  trash?: boolean;
  handleAction?: ActionHandlerFunction;
}

function BreadcrumbsComponent(props: AllProps) {
  const { current_account, path_info, id, collection_info, trash } = props;
  const root_collection_id = useAppSelector((state: RootState) => state.drive.root_info?.collection_id);
  const [effective_root, setEffectiveRoot] = useState<PathInfo | undefined>();
  const { link_entity, fetchLinkEntity } = useFetchLinkItem(current_account);

  useEffect(() => {
    if (!!path_info && path_info.length > 0 && !!collection_info) {
      buildEffectiveRootNode(path_info[0], collection_info);
      (path_info.length > 1 && path_info[0].path !== DefaultCollectionName && !!root_collection_id && root_collection_id !== collection_info.collection_id) &&
        fetchLinkEntity(collection_info.collection_id);
    }
  }, [path_info]);

  // Description: Perform all checks to build the ACL Node Effective root
  function buildEffectiveRootNode(info: PathInfo, _collection_info: CollectionEntity) {
    const id = info.id;
    const maintainer_id = _collection_info.maintainer_id;
    // NOTE: Need to check this collection_info is NOT root so it won't duplicate node on owner accts
    if (isSameIdentifier(id, maintainer_id) && _collection_info?.disjointed_acl_node) {
      fetchLinkEntity(_collection_info.collection_id);
      setEffectiveRoot({
        id,
        path: _collection_info.collection_name,
        url: DefaultRoutes.drive_root, // NOTE: Will build the correct url after link_entity comes back
        type: DriveEntryType.LINK
      });
    } else {
      setEffectiveRoot(undefined);
    }
  }

  // Description: Build link / first node url 
  function getLinkEntityURL(node: PathInfo) {
    return !!link_entity ? buildNavigationLink(link_entity.collection_id, link_entity.id, DriveEntryType.LINK) :
      (node.url || DefaultRoutes.drive_root);
  }

  // Description: Return the breadcrumb item without the root title
  function BreadcrumbItem(props: { node: PathInfo, index: number, length: number }) {
    const { node, index, length } = props;
    // NOTE: Check if the node should be rendered. Do not render if the path is a true root, only its link or if its the defaultCollection node
    const render_node = !node.is_true_root && (node.path !== DefaultCollectionName && node.path !== DriveConstants.ROOT_DIRECTORY);
    return render_node ?
      <Breadcrumb.Item
        // NOTE: Check to see if this is the last node which is current node and make active
        active={node.id === id || (index + 1 >= length)}
        linkAs={Link}
        linkProps={{ to: (index === 0 && length > 1) ? getLinkEntityURL(node) : (node.url || DefaultRoutes.drive_root) }}>
        {node.path}
      </Breadcrumb.Item> : null;
  }

  // Description: Return the TRASH breadcrumb item without the root title, or LINK, true root directory nodes (only regular folders)
  function TrashBreadcrumbItem(props: { node: PathInfo, index: number, length: number }) {
    const { node, index, length } = props;
    // Description: Check if the node should be rendered. Do not render if the path is a LINK, a true root, only its link or if its the defaultCollection node
    const render_trash_node = !node.is_true_root && (node.path !== DefaultCollectionName && node.path !== DriveConstants.ROOT_DIRECTORY);
    // const render_trash_node = node.type !== DriveEntryType.LINK && !node.is_true_root && (node.path !== DefaultCollectionName && node.path !== DriveConstants.TRASH_DRIVE);
    return (render_trash_node && node.type !== DriveEntryType.LINK) ?
      <Breadcrumb.Item
        // NOTE: Check to see if this is the last node which is current node and make active
        active={node.id === id || (index + 1 >= length)}
        linkAs={Link}
        linkProps={{ to: (index === 0 && length > 1) ? getLinkEntityURL(node) : (node.url || DefaultRoutes.drive_root) }}>
        {node.path}
      </Breadcrumb.Item> : null;
  }

  // Description: Return the string for the title node
  function getRootPathName() {
    return trash ? DriveConstants.TRASH_DRIVE : DriveConstants.ROOT_DIRECTORY;
  }

  return <Breadcrumb className="drive-breadcrumbs">
    <Breadcrumb.Item className="h1"
      linkProps={{ to: trash ? DefaultRoutes.drive_trash : DefaultRoutes.drive_root }}
      linkAs={Link}>{getRootPathName()}</Breadcrumb.Item>
    {
      (!!path_info && path_info.length > 0) &&
      <>
        {
          !!effective_root &&
          <Breadcrumb.Item
            linkAs={Link}
            linkProps={{ to: !!link_entity ? buildNavigationLink(link_entity.collection_id, link_entity.id, DriveEntryType.LINK) : DefaultRoutes.drive_root }}>
            {effective_root.path}</Breadcrumb.Item>
        }
        {
          _.map(path_info, (node: PathInfo, index: number) => {
            return !!trash ? <TrashBreadcrumbItem key={`path_${index}`} node={node} index={index} length={path_info.length} /> :
              <BreadcrumbItem key={`path_${index}`} node={node} index={index} length={path_info.length} />;
          })
        }
      </>
    }
  </Breadcrumb >;
}

export default React.memo(BreadcrumbsComponent);
